import { legacyGlobals, legacyState } from '~/legacy/screens'
import { render } from '~/legacy/runtime'
import type { Row } from '~/composables/useDb'

/**
 * Écritures des pages Notes et Bulletins. Le code de la maquette modifiait sa base en mémoire ;
 * ici, chaque écriture passe par l'API Java, avec les mêmes règles (mêmes messages, mêmes bornes).
 */
type NotesPicker = { classeId: string, matiereId: string, periodeId: string, pendingColumns: { typeEvaluation: string, dateEvaluation: string }[] }
const picker = (): NotesPicker => legacyState.notesPicker as unknown as NotesPicker

function errorMessage(): string {
  return 'Opération impossible.'
}

/** `removeNotesColumn` de la maquette : retire une évaluation, avec toutes ses notes. */
export async function removeNotesColumn(type: string, date: string): Promise<void> {
  const p = picker()
  p.pendingColumns = (p.pendingColumns || []).filter(c => !(c.typeEvaluation === type && c.dateEvaluation === date))
  const db = useDb().value
  const inscIds = (db.inscriptions ?? []).filter(i => i.classeId === p.classeId).map(i => i.id)
  const targets = (db.notes ?? []).filter(n => n.matiereId === p.matiereId && n.periodeId === p.periodeId
    && n.typeEvaluation === type && n.dateEvaluation === date && inscIds.includes(n.inscriptionId as string))
  try {
    for (const n of targets) await apiRemove('notes', n.id)
  }
  catch (e) {
    console.error('[Registre] usePedagogie:', e)
    showToast(errorMessage())
    return
  }
  showToast(targets.length ? `Évaluation supprimée avec ses ${targets.length} note(s).` : 'Colonne retirée.')
  render()
}

/** `saveNotesGrid` de la maquette : enregistre la grille de saisie (création, modification, suppression des cases vidées). */
export async function saveNotesGrid(classeId: string, matiereId: string, periodeId: string): Promise<void> {
  const inputs = document.querySelectorAll<HTMLInputElement>('.grade-input[data-insc]')
  let saved = 0
  let cleared = 0
  try {
    for (const input of inputs) {
      const inscId = input.dataset.insc
      const type = input.dataset.type!
      const date = input.dataset.date!
      const raw = input.value
      const existing = (useDb().value.notes ?? []).find(n => n.inscriptionId === inscId && n.matiereId === matiereId
        && n.periodeId === periodeId && n.typeEvaluation === type && n.dateEvaluation === date)
      if (raw === '') {
        if (existing) {
          await apiRemove('notes', existing.id)
          cleared++
        }
        continue
      }
      const valeur = Math.max(0, Math.min(20, Number(raw)))
      if (existing) {
        if (Number(existing.valeur) !== valeur) await apiUpdate('notes', existing.id, { ...withoutId(existing), valeur })
      }
      else {
        await apiCreate('notes', { inscriptionId: inscId, matiereId, periodeId, typeEvaluation: type, dateEvaluation: date, valeur })
      }
      saved++
    }
  }
  catch (e) {
    console.error('[Registre] usePedagogie:', e)
    showToast(errorMessage())
    return
  }
  picker().pendingColumns = []
  showToast(`${saved} note(s) enregistrée(s)${cleared ? `, ${cleared} supprimée(s)` : ''}.`)
  render()
}

/** `generateBulletin` de la maquette : calcule la moyenne, les crédits et le rang, puis crée ou met à jour le bulletin. */
export async function generateBulletin(inscriptionId: string, periodeId: string, appreciation?: string): Promise<Row | null> {
  const g = legacyGlobals as Record<string, (...args: string[]) => unknown>
  const inscription = findById('inscriptions', inscriptionId)
  const classe = inscription && inscription.classeId ? findById('classes', inscription.classeId) : null
  if (!classe) return null
  const moyenne = g.computeBulletinMoyenne!(inscriptionId, periodeId, classe.id) as number | null
  if (moyenne == null) return null
  const rounded = Math.round(moyenne * 100) / 100
  const credits = g.computeCreditsValides!(inscriptionId, periodeId, classe.id) as number
  const { rang } = g.computeRang!(inscriptionId, periodeId, classe.id) as { rang: number | null }
  const existing = (useDb().value.bulletins ?? []).find(b => b.inscriptionId === inscriptionId && b.periodeId === periodeId)
  if (existing) {
    return apiUpdate('bulletins', existing.id, {
      ...withoutId(existing), moyenne: rounded, creditsValides: credits, rang, ...(appreciation !== undefined ? { appreciation } : {}),
    })
  }
  return apiCreate('bulletins', {
    inscriptionId, periodeId, moyenne: rounded, creditsValides: credits, rang, appreciation: appreciation ?? '', dateEmission: new Date().toISOString().slice(0, 10),
  })
}

/** `handleGenerateBulletin` de la maquette (bouton « Générer » / « Recalculer » de la fiche). */
export async function handleGenerateBulletin(inscriptionId: string, periodeId: string): Promise<void> {
  try {
    const b = await generateBulletin(inscriptionId, periodeId)
    if (!b) {
      showToast('Aucune note trouvée pour cette période, impossible de générer le bulletin.')
      return
    }
  }
  catch (e) {
    console.error('[Registre] usePedagogie:', e)
    showToast(errorMessage())
    return
  }
  showToast('Bulletin généré/recalculé.')
  render()
}

/** `saveBulletinRow` de la maquette : un bulletin, avec l'appréciation saisie. */
export async function saveBulletinRow(inscriptionId: string, periodeId: string): Promise<void> {
  const appr = (document.getElementById(`appr-${inscriptionId}`) as HTMLInputElement | null)?.value ?? ''
  try {
    const b = await generateBulletin(inscriptionId, periodeId, appr)
    if (!b) {
      showToast('Aucune note trouvée pour cet élève sur cette période, bulletin non généré.')
      return
    }
  }
  catch (e) {
    console.error('[Registre] usePedagogie:', e)
    showToast(errorMessage())
    return
  }
  showToast('Bulletin mis à jour pour cet élève.')
  render()
}

/** `saveAllBulletins` de la maquette : tous les bulletins de la classe pour la période. */
export async function saveAllBulletins(classeId: string, periodeId: string): Promise<void> {
  const btn = document.getElementById('btn-generer-tous-bulletins') as HTMLButtonElement | null
  // Verrou anti-double-clic : sans lui, un second clic pendant le traitement relance une boucle en parallèle
  // (aucun retour visuel entre-temps) et génère des bulletins en double.
  if (btn?.disabled) return
  if (btn) {
    btn.disabled = true
    btn.textContent = 'Génération…'
  }
  const inscriptions = (useDb().value.inscriptions ?? []).filter(i => i.classeId === classeId && i.statut === 'VALIDEE')
  let generated = 0
  let skipped = 0
  let failed = 0
  // Un élève en échec (note invalide, conflit, etc.) ne doit pas interrompre les suivants :
  // chaque élève est tenté indépendamment, et le tableau est toujours rafraîchi avec ce qui a pu être fait.
  for (const insc of inscriptions) {
    const appr = (document.getElementById(`appr-${insc.id}`) as HTMLInputElement | null)?.value ?? ''
    try {
      const b = await generateBulletin(insc.id, periodeId, appr)
      if (b) generated++
      else skipped++
    }
    catch (e) {
      console.error('[Registre] saveAllBulletins, inscription', insc.id, ':', e)
      failed++
    }
  }
  if (btn) {
    btn.disabled = false
    btn.textContent = 'Générer tous les bulletins'
  }
  const parts = [`${generated} bulletin(s) généré(s)/mis à jour`]
  if (skipped) parts.push(`${skipped} sans note`)
  if (failed) parts.push(`${failed} en échec`)
  showToast(`${parts.join(', ')}.`)
  render()
}
